
import { getSupabaseClient, isSupabaseInitialized } from './client';

/**
 * Ensures Supabase is initialized before returning the client
 * This prevents errors when components try to access Supabase before initialization completes
 */
export const getSafeClient = async () => {
  // Check if already initialized
  if (isSupabaseInitialized()) {
    return getSupabaseClient();
  }
  
  // Not initialized, wait for it with timeout
  let attempts = 0;
  const maxAttempts = 30; // Increased from 10 to 30 attempts
  const delayMs = 200; // Increased from 100ms to 200ms
  
  while (!isSupabaseInitialized() && attempts < maxAttempts) {
    // Wait between checks
    await new Promise(resolve => setTimeout(resolve, delayMs));
    attempts++;
    
    console.log(`Waiting for Supabase to initialize... (attempt ${attempts}/${maxAttempts})`);
    
    // Try to initialize if not already in progress
    try {
      // Avoid direct circular dependency by using an intermediary promise
      await new Promise<void>((resolve, reject) => {
        // Call getSupabaseClient() as a function that returns a promise
        getSupabaseClient()
          .then(() => resolve())
          .catch(err => reject(err));
      });
    } catch (error) {
      console.warn(`Initialization attempt ${attempts} failed:`, error);
    }
  }
  
  if (!isSupabaseInitialized()) {
    console.error('Supabase failed to initialize after multiple attempts');
    throw new Error('Database connection could not be established. Please refresh the page.');
  }
  
  return getSupabaseClient();
};

/**
 * Wraps a Supabase query in a safe execution context
 * Ensures Supabase is initialized and handles errors consistently
 * 
 * @example
 * // Basic usage
 * const { data, error } = await safeQuery(
 *   async (client) => client.from('tasks').select('*')
 * );
 * 
 * // With fallback value
 * const { data, error } = await safeQuery(
 *   async (client) => client.from('tasks').select('*'),
 *   { data: [], error: null }
 * );
 */
export const safeQuery = async <T>(
  queryFn: (client: ReturnType<typeof getSupabaseClient>) => Promise<T>,
  fallbackValue?: T
): Promise<T> => {
  try {
    const client = await getSafeClient();
    return await queryFn(client);
  } catch (error) {
    console.error('Error executing Supabase query:', error);
    
    if (fallbackValue !== undefined) {
      console.log('Using fallback value due to query error');
      return fallbackValue;
    }
    
    throw error;
  }
};
